import { Plus, X } from "lucide-react";

const COBERTURAS = ["Particular", "Urgencias"];
const DIAS_LABEL = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

// Chip — used in the day selector grid row
export function DiaChip({ dia, isSelected, onSelect }) {
  const fechaDisplay = dia.fecha
    ? new Date(dia.fecha + "T00:00:00Z").toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      })
    : "";

  const chipClass = () => {
    if (dia.es_feriado) return "bg-red-50 text-red-700 border-red-200";
    if (!dia.activo) return "bg-slate-100 text-slate-400 border-slate-200";
    return "bg-blue-50 text-blue-700 border-blue-200";
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full border rounded-xl px-2 py-2.5 text-center transition-all ${chipClass()} ${
        isSelected ? "ring-2 ring-blue-500 ring-offset-1" : "hover:brightness-95"
      }`}
    >
      <p className="text-xs font-bold">{DIAS_LABEL[dia.dia_semana].slice(0, 3)}</p>
      <p className="text-xs mt-0.5">{fechaDisplay}</p>
      {dia.es_feriado && (
        <p className="text-[10px] mt-1 font-medium text-red-600">Feriado</p>
      )}
      {!dia.activo && !dia.es_feriado && (
        <p className="text-[10px] mt-1 text-slate-400">Inactivo</p>
      )}
      {dia.activo && !dia.es_feriado && dia.franjas_horarias.length > 0 && (
        <p className="text-[10px] mt-1 font-medium">
          {dia.franjas_horarias.length} franja{dia.franjas_horarias.length > 1 ? "s" : ""}
        </p>
      )}
    </button>
  );
}

// Config panel — shown below the grid for the selected day
export default function DiaConfigCard({ dia, onChange }) {
  const fechaDisplay = dia.fecha
    ? new Date(dia.fecha + "T00:00:00Z").toLocaleDateString("es-AR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone: "UTC",
      })
    : "";

  const addFranja = () => {
    onChange({
      ...dia,
      franjas_horarias: [...dia.franjas_horarias, { inicio: "09:00", fin: "13:00" }],
    });
  };

  const removeFranja = (idx) => {
    onChange({
      ...dia,
      franjas_horarias: dia.franjas_horarias.filter((_, i) => i !== idx),
    });
  };

  const updateFranja = (idx, field, value) => {
    onChange({
      ...dia,
      franjas_horarias: dia.franjas_horarias.map((f, i) =>
        i === idx ? { ...f, [field]: value } : f
      ),
    });
  };

  const toggleCobertura = (cobertura) => {
    const current = dia.coberturas_habilitadas;
    const updated = current.includes(cobertura)
      ? current.filter((c) => c !== cobertura)
      : [...current, cobertura];
    onChange({ ...dia, coberturas_habilitadas: updated });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm space-y-5">
      <p className="font-bold text-slate-900 capitalize">{fechaDisplay}</p>

      {/* Activo toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-700">Atiende este día</span>
        <button
          type="button"
          onClick={() => onChange({ ...dia, activo: !dia.activo })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            dia.activo ? "bg-blue-600" : "bg-slate-200"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              dia.activo ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Feriado toggle */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-700">Es feriado</span>
          <button
            type="button"
            onClick={() =>
              onChange({
                ...dia,
                es_feriado: !dia.es_feriado,
                activo: !dia.es_feriado ? false : dia.activo,
              })
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              dia.es_feriado ? "bg-red-500" : "bg-slate-200"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                dia.es_feriado ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {dia.es_feriado && (
          <input
            className="input-field text-sm"
            placeholder="Motivo del feriado (ej: Día de la Bandera)"
            value={dia.feriado_motivo || ""}
            onChange={(e) => onChange({ ...dia, feriado_motivo: e.target.value })}
          />
        )}
      </div>

      {/* Franjas horarias */}
      {dia.activo && !dia.es_feriado && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            Franjas horarias
          </p>
          {dia.franjas_horarias.length === 0 && (
            <p className="text-xs text-slate-400 italic">Sin franjas configuradas</p>
          )}
          {dia.franjas_horarias.map((franja, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="time"
                className="input-field text-sm flex-1"
                value={franja.inicio}
                onChange={(e) => updateFranja(idx, "inicio", e.target.value)}
              />
              <span className="text-slate-400 text-sm flex-shrink-0">–</span>
              <input
                type="time"
                className="input-field text-sm flex-1"
                value={franja.fin}
                onChange={(e) => updateFranja(idx, "fin", e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeFranja(idx)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addFranja}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
          >
            <Plus size={14} />
            Agregar franja
          </button>
        </div>
      )}

      {/* Coberturas */}
      {dia.activo && !dia.es_feriado && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            Coberturas habilitadas
          </p>
          <div className="flex flex-wrap gap-2">
            {COBERTURAS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggleCobertura(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  dia.coberturas_habilitadas.includes(c)
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-500 border-slate-200 hover:border-blue-300"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
